import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useToast } from "@shared/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/ui/dialog";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useCreateResourceType, useEditResourceTypeSubtype } from "../../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface ResourceTypeFormProps {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  editData?: any;
}

export function ResourceTypeForm({
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  editData,
}: ResourceTypeFormProps) {
  const { toast } = useToast();
  const [localOpen, setLocalOpen] = React.useState(false);
  const createMutation = useCreateResourceType();
  const editMutation = useEditResourceTypeSubtype();
  const createResourceType = createMutation.mutate;
  const editResourceType = editMutation.mutate;
  const isLoading = Boolean(createMutation.isPending || editMutation.isPending || createMutation.isLoading || editMutation.isLoading);
  const remoteUtilityId = useRemoteUtilityId();

  const open = controlledOpen !== undefined ? controlledOpen : localOpen;
  const setOpen = controlledOnOpenChange !== undefined ? controlledOnOpenChange : setLocalOpen;
  const isEditMode = Boolean(editData);

  const getInitialValues = () => {
    if (!editData) {
      return {
        name: "",
        description: "",
        supportsCycles: "true",
        supportsServiceOrders: "true",
      };
    }

    return {
      name: editData.name || "",
      description: editData.description || "",
      supportsCycles: editData.capabilities?.captureReads ? "true" : "false",
      supportsServiceOrders: editData.capabilities?.supportsServiceOrders ? "true" : "false",
    };
  };

  const formFields: FormField[] = [
    // Basic Info Group
    {
      name: "name",
      type: "text",
      label: "Name",
      placeholder: "e.g. Meter, Trash Can, Water Container",
      required: true,
      group: "Basic Info",
      groupColorNeeded: false,
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      placeholder: "Brief description...",
      rows: 2,
      group: "Basic Info",
      className: "mt-4",
    },
    // Capabilities Group
    {
      name: "supportsCycles",
      type: "radio",
      label: "Capture Reads?",
      group: "Capabilities",
      className: "grid grid-cols-2 items-center py-3 border-b border-gray-100 [&>.form-radio-group]:ml-auto",
      options: [
        { label: "Yes", value: "true" },
        { label: "No", value: "false" },
        
      ],
            groupColorNeeded: false,

    },
    {
      name: "supportsServiceOrders",
      type: "radio",
      label: "Supports Service Orders?",
      group: "Capabilities",
      className: "grid grid-cols-2 items-center py-3 [&>.form-radio-group]:ml-auto",
      options: [
        { label: "Yes", value: "true" },
        { label: "No", value: "false" },
      ],
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    defaultValues: getInitialValues(),
  });

  // Reset form when dialog opens/closes or editData changes
  useEffect(() => {
    if (open) {
      form.reset(getInitialValues());
    }
  }, [open, editData]);

  const handleSubmit = (data: any) => {
    if (!data.name.trim()) {
      toast({
        title: "Error",
        description: "Please enter a resource type name",
        variant: "destructive",
      });
      return;
    }

    const payload: any = {
      name: data.name,
      code: data.name,
      remote_utility_id: remoteUtilityId,
      description:data.description,
      capabilities: {
        capture_reads: data.supportsCycles === "true" ? 1 : 0,
        supports_service_orders: data.supportsServiceOrders === "true" ? 1 : 0,
      },
    };

    if (isEditMode) {
      payload.code = editData?.code;
      editResourceType(payload, {
        onSuccess: (response) => {
          setOpen(false);
        },
      });
    } else {
      createResourceType(payload, {
        onSuccess: (response) => {
          setOpen(false);
        },
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit Resource Type" : "Add Resource Type"}</DialogTitle>
        </DialogHeader>

        <DynamicForm
          fields={formFields}
          form={form}
          onSubmit={handleSubmit}
          onReset={() => setOpen(false)}
          loading={isLoading}
          config={{
            showResetButton: true,
            showSubmitButton: true,
            submitButtonText: isEditMode ? "Update Resource Type" : "Add Resource Type",
            resetButtonText: "Cancel",
            classes: FIELD_CLASSES,
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
export default ResourceTypeForm;
